<template>
  <figure
    ref="elRef"
    class="PlayerAvatarList"
    data-testid="playerAvatarList"
    :class="{
      'PlayerAvatarList--isInactive': !isActive,
    }"
    :aria-busy="!isReady"
  >
    <TransitionGroup v-if="isReady">
      <PlayerAvatar
        v-for="(player, i) in avatarPlayers"
        :key="String(i)"
        class="PlayerAvatarList__item"
        :modelValue="player"
        :style="`--index: ${i}`"
      />
    </TransitionGroup>
    <Transition>
      <div
        v-if="isTruncated"
        key="count"
        class="PlayerAvatarList__count"
      >
        +{{ players.length - truncateCount }}
      </div>
    </Transition>
  </figure>
</template>

<script setup lang="ts">
import { PlayerSummary } from "@shared/lib/database/query.types";
import { computed, onBeforeUnmount, onMounted, ref, useTemplateRef, watch } from "vue";
import PlayerAvatar from "./PlayerAvatar.vue";

const players = defineModel<Array<PlayerSummary>>({
  required: true,
});

const {
  justify = "start",
  minCount = 0,
  isActive = true,
} = defineProps<{
  justify?: "start" | "center" | "end",
  minCount?: number,
  isActive?: boolean,
}>();

const elRef = useTemplateRef("elRef");
const resizeObserver = new ResizeObserver(handleResize);
const avatarSize = 48;
const avatarOffset = 12;
const truncateCount = ref(8);
const avatarFitCount = ref(8);
const isReady = ref(false);

onMounted(() => {
  if (elRef.value?.parentElement) resizeObserver.observe(elRef.value.parentElement);
});

onBeforeUnmount(() => {
  resizeObserver.disconnect();
});

watch(players, () => {
  handleResize();
});

const isTruncated = computed(() => {
  return players.value.length > truncateCount.value;
});

const truncatedPlayers = computed(() => {
  return players.value.slice(0, truncateCount.value) || [];
});

const avatarPlayers = computed((): Array<PlayerSummary | null> => {
  if (!minCount || isTruncated.value) return truncatedPlayers.value;

  const placeholders = Array(Math.max(0, minCount - truncatedPlayers.value.length)).fill(null);

  return [...truncatedPlayers.value, ...placeholders].slice(0, avatarFitCount.value);
});

function handleResize () {
  const containerEl = elRef.value?.parentElement as HTMLElement | undefined;
  if (!containerEl) return;

  avatarFitCount.value = Math.max(0, Math.ceil((containerEl.offsetWidth - avatarOffset) / (avatarSize - avatarOffset)) - 1);

  truncateCount.value = players.value.length <= avatarFitCount.value
    ? players.value.length
    : avatarFitCount.value - 1;

  isReady.value = true;
}
</script>

<style lang="scss" scoped>
.PlayerAvatarList {
  --transition: 350ms var(--easeOutBack);
  --itemHeight: 48px;
  position: relative;
  isolation: isolate;

  display: flex;
  justify-content: v-bind(justify);
  align-items: center;
  height: var(--itemHeight);
}

.PlayerAvatarList--isInactive {
  filter: grayscale(100%);
}

.PlayerAvatarList__item {
  z-index: calc(-1 * var(--index));
  transition: var(--transition);
  transition-property: opacity, transform;

  &.v-leave-active {
    position: absolute;
    left: calc(v-bind(avatarSize) * var(--index) * 1px - v-bind(avatarOffset) * max(0, var(--index) - 1) * 1px);
  }

  &.v-enter-from,
  &.v-leave-to {
    transform: scale(calc((var(--itemHeight) - 4px) / var(--itemHeight)));
    opacity: 0;
  }

  & + & {
    margin-left: calc(-1px * v-bind(avatarOffset));
  }
}

.PlayerAvatarList__count {
  --shadowColor: var(--themeDarkTransparent100);
  --shadowWidth: 4px;

  position: relative;

  display: grid;
  flex: 0 0 auto;
  place-items: center;
  width: 40px;
  aspect-ratio: 1;
  margin-left: calc(-1px * v-bind(avatarOffset) + var(--shadowWidth));
  padding-top: calc(1px + var(--fontSafariOffset));
  padding-right: 1px;

  border-radius: 50%;
  background-color: var(--themeColorWhite);
  color: var(--themeColorNeutral900);
  box-shadow: 0 0 0 var(--shadowWidth) var(--shadowColor);

  font: var(--fontWeightMedium) 16px / 1 var(--fontFamilyMono);
  font-stretch: var(--fontWidthExtended);
  text-align: center;

  transition: var(--transition);
  transition-property: opacity, transform;

  [data-theme="dark"] & {
    --shadowColor: var(--themeColorSurface);
    --shadowWidth: 3px;
    background-color: var(--themeColorNeutral0);
  }

  &.v-leave-active {
    position: absolute;
    left: calc(v-bind(avatarSize) * v-bind(avatarFitCount) * 1px - v-bind(avatarOffset) * (v-bind(avatarFitCount) + 2) * 1px);
    z-index: 1;
  }

  &.v-enter-from,
  &.v-leave-to {
    transform: scale(calc((4px + var(--itemHeight)) / var(--itemHeight)));
    opacity: 0;
  }
}
</style>
